// ================================================================
// MobileSheet — das EINE Bottom-Sheet der Mobile Lead-UI.
// ================================================================
// Jedes Sheet (Einstellungen, Offerte senden, künftige) nutzt DIESE
// Hülle statt eigener wrap/handle/title-Kopien: Overlay-Tap, ✕-Button
// oben rechts und Runterziehen (Drag ≥ 110px) schliessen einheitlich.
// Per Portal auf document.body (Projekt-Regel für Modals) — so liegt
// das Sheet auch dann über der Bottom-Nav, wenn es aus einem Overlay
// mit eigenem Stacking-Context (z.B. MobileOfferSheet) geöffnet wird.
import { useId, useRef, useState, type ReactNode, type TouchEvent } from 'react';
import { createPortal } from 'react-dom';
import { useModalFocus } from '../../lib/useModalFocus';
import './MobileSheet.css';

export default function MobileSheet({ title, onClose, closeLabel, children, footer }: {
  title: ReactNode;
  onClose: () => void;
  /** Übersetztes aria-label des ✕-Buttons (t('common.close')) */
  closeLabel: string;
  children: ReactNode;
  /** Optionaler Fuss (z.B. Cancel/Save), der NICHT mitscrollt, sondern immer
      am unteren Sheet-Rand klebt. Mit Fuss scrollt nur der Body — auf iOS die
      einzig verlässliche Variante (position:sticky + Safe-Area lässt sonst
      einen Spalt, durch den Inhalt durchscheint). */
  footer?: ReactNode;
}) {
  const sheetRef = useRef<HTMLDivElement | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const titleId = useId();
  useModalFocus(dialogRef, onClose);
  const startY = useRef<number | null>(null);
  // Aktueller Zieh-Abstand als Ref: der Touchend-Handler darf nicht vom
  // (evtl. veralteten) State des letzten Renders abhängen.
  const dyRef = useRef(0);
  const [dragY, setDragY] = useState(0);
  const [dragging, setDragging] = useState(false);

  // Drag nur starten, wenn der scrollende Bereich ganz oben steht — sonst will
  // der User scrollen (Sprachliste!), nicht schliessen. Mit Fuss ist das der
  // innere Body, sonst das Sheet selbst (sheetRef zeigt jeweils dorthin).
  const onTouchStart = (e: TouchEvent) => {
    if ((sheetRef.current?.scrollTop ?? 0) > 0) return;
    startY.current = e.touches[0].clientY;
    dyRef.current = 0;
    setDragging(true);
  };
  const onTouchMove = (e: TouchEvent) => {
    if (startY.current == null) return;
    dyRef.current = Math.max(0, e.touches[0].clientY - startY.current);
    setDragY(dyRef.current);
  };
  const onTouchEnd = () => {
    if (startY.current == null) return;
    const dy = dyRef.current;
    startY.current = null;
    dyRef.current = 0;
    setDragging(false);
    // A guarded editor may refuse to close; always restore its resting position.
    setDragY(0);
    if (dy > 110) onClose();
  };

  return createPortal(
    <div className="lm-sheet-wrap" onClick={onClose}>
      <div
        // Ohne Fuss scrollt das Sheet selbst → sheetRef hier. Mit Fuss scrollt
        // der innere Body → sheetRef dort (siehe unten).
        ref={(node) => { dialogRef.current = node; if (!footer) sheetRef.current = node; }}
        role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1}
        className={'lm-sheet' + (footer ? ' has-footer' : '') + (dragging ? ' dragging' : '')}
        style={dragY ? { transform: `translateY(${dragY}px)` } : undefined}
        onClick={(e) => e.stopPropagation()}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchEnd}
      >
        <div className="lm-sheet-handle" />
        <button type="button" className="lm-sheet-x" onClick={onClose} aria-label={closeLabel}>
          <span className="material-symbols-rounded" aria-hidden="true">close</span>
        </button>
        <div className="lm-sheet-title" id={titleId}>{title}</div>
        {footer ? (
          <>
            <div className="lm-sheet-body" ref={sheetRef}>{children}</div>
            <div className="lm-sheet-footer">{footer}</div>
          </>
        ) : children}
      </div>
    </div>,
    document.body,
  );
}
